import { ref, provide, defineComponent, inject, watch, reactive, computed, watchEffect } from 'vue';
import { mergeDefaultValue } from './Image';
import Preview from './Preview';
import useMergedState from '../../_util/hooks/useMergedState';
const previewGroupContext = Symbol('previewGroupContext');
export const context = {
    provide: (val) => {
        provide(previewGroupContext, val);
    },
    inject: () => {
        return inject(previewGroupContext, {
            isPreviewGroup: ref(false),
            previewUrls: computed(() => new Map()),
            setPreviewUrls: () => { },
            current: ref(null),
            setCurrent: () => { },
            setShowPreview: () => { },
            setMousePosition: () => { },
            registerImage: null,
            rootClassName: '',
        });
    },
};
const Group = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'PreviewGroup',
    inheritAttrs: false,
    props: {
        previewPrefixCls: String,
        preview: {
            type: [Boolean, Object],
            default: true,
        },
        icons: {
            type: Object,
            default: () => ({}),
        },
    },
    setup(props, { slots }) {
        const preview = computed(() => {
            const defaultValues = {
                visible: undefined,
                onVisibleChange: () => { },
                getContainer: undefined,
                current: 0,
            };
            return typeof props.preview === 'object'
                ? mergeDefaultValue(props.preview, defaultValues)
                : defaultValues;
        });
        const previewUrls = reactive(new Map());
        const current = ref();
        const previewVisible = computed(() => preview.value.visible);
        const getPreviewContainer = computed(() => preview.value.getContainer);
        const onPreviewVisibleChange = (val, preval) => {
            preview.value.onVisibleChange?.(val, preval);
        };
        const [isShowPreview, setShowPreview] = useMergedState(!!previewVisible.value, {
            value: previewVisible,
            onChange: onPreviewVisibleChange,
        });
        const mousePosition = ref(null);
        const isControlled = computed(() => previewVisible.value !== undefined);
        const previewUrlsKeys = computed(() => Array.from(previewUrls.keys()));
        const currentControlledKey = computed(() => previewUrlsKeys.value[preview.value.current]);
        const canPreviewUrls = computed(() => new Map(Array.from(previewUrls)
            .filter(([, { canPreview }]) => !!canPreview)
            .map(([id, { url }]) => [id, url])));
        const setPreviewUrls = (id, url, canPreview = true) => {
            previewUrls.set(id, {
                url,
                canPreview,
            });
        };
        const setCurrent = (val) => {
            current.value = val;
        };
        const setMousePosition = (val) => {
            mousePosition.value = val;
        };
        const registerImage = (id, url, canPreview = true) => {
            const unRegister = () => {
                previewUrls.delete(id);
            };
            previewUrls.set(id, {
                url,
                canPreview,
            });
            return unRegister;
        };
        const onPreviewClose = (e) => {
            e?.stopPropagation();
            setShowPreview(false);
            setMousePosition(null);
        };
        watch(currentControlledKey, val => {
            setCurrent(val);
        }, {
            immediate: true,
            flush: 'post',
        });
        watchEffect(() => {
            if (isShowPreview.value && isControlled.value) {
                setCurrent(currentControlledKey.value);
            }
        }, {
            flush: 'post',
        });
        context.provide({
            isPreviewGroup: ref(true),
            previewUrls: canPreviewUrls,
            setPreviewUrls,
            current,
            setCurrent,
            setShowPreview,
            setMousePosition,
            registerImage,
        });
        return () => {
            const { ...dialogProps } = preview.value;
            return (<>
          {slots.default && slots.default()}
          <Preview {...dialogProps} ria-hidden={!isShowPreview.value} visible={isShowPreview.value} prefixCls={props.previewPrefixCls} onClose={onPreviewClose} mousePosition={mousePosition.value} src={canPreviewUrls.value.get(current.value)} icons={props.icons} getContainer={getPreviewContainer.value}/>
        </>);
        };
    },
});
export default Group;
